/**
 * EDR — Agent enrollment hooks.
 *
 * Backs the Settings → Enrollment panel. All three queries tolerate a
 * 404 from the backend (endpoint not shipped yet) by returning an
 * empty payload so the UI shows an empty state instead of an error.
 *
 * Spec: docs/backend-requirements/edr-agent-enrollment-api.md
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { edrGet, edrPatch, edrPost, EdrApiError } from "@/services/edrApi";
import { useConnectorEnabled } from "@/hooks/connectors/useConnector";

export type EnrollmentStatus =
  | "pending"
  | "enrolled"
  | "expired"
  | "revoked"
  | "failed";

export type EnrollmentPlatform = "macos" | "windows" | "linux";

export interface EnrollmentSummary {
  id: string;
  label?: string | null;
  platform?: EnrollmentPlatform | null;
  status: EnrollmentStatus;
  createdAt: string;
  expiresAt?: string | null;
  enrolledAt?: string | null;
  deviceId?: string | null;
  deviceName?: string | null;
}

export interface EnrollmentDeviceInfo {
  id: string;
  hostname?: string | null;
  platform?: string | null;
  osVersion?: string | null;
  arch?: string | null;
  agentVersion?: string | null;
  serialNumber?: string | null;
  publicIp?: string | null;
  privateIp?: string | null;
  firstSeenAt?: string | null;
  lastSeenAt?: string | null;
  consoleUser?: string | null;
}

export interface EnrollmentDetail extends EnrollmentSummary {
  device?: EnrollmentDeviceInfo | null;
}

export interface MintEnrollmentInput {
  label?: string;
  platform?: EnrollmentPlatform;
  expiresInHours?: number;
}

export interface MintEnrollmentResult extends EnrollmentSummary {
  /** Returned once at mint time — never appears in subsequent reads. */
  code: string;
  installCommand?: string | null;
}

const BASE = "/api/v1/connectors/edr/enrollments";
const LIST_KEY = ["edr", "enrollments"] as const;

const STALE_MS = 15_000;
const POLL_MS = 10_000;

interface ListResponse {
  enrollments?: EnrollmentSummary[];
}

/** Sentinel: backend hasn't shipped the endpoints yet. */
export const ENROLLMENT_UNAVAILABLE = Symbol("enrollment-unavailable");
export type EnrollmentListResult =
  | { kind: "ok"; rows: EnrollmentSummary[] }
  | { kind: "unavailable" };

export function useEdrEnrollments() {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: LIST_KEY,
    staleTime: STALE_MS,
    refetchInterval: POLL_MS,
    queryFn: async (): Promise<EnrollmentListResult> => {
      try {
        const data = await edrGet<ListResponse | EnrollmentSummary[]>(BASE);
        const rows = Array.isArray(data) ? data : data?.enrollments ?? [];
        return { kind: "ok", rows };
      } catch (err) {
        if (err instanceof EdrApiError && err.status === 404) {
          return { kind: "unavailable" };
        }
        throw err;
      }
    },
  });
}

export function useEdrEnrollmentDetail(id: string | null) {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled: enabled && !!id,
    queryKey: ["edr", "enrollments", "detail", id],
    staleTime: STALE_MS,
    refetchInterval: POLL_MS,
    queryFn: async (): Promise<EnrollmentDetail | null> => {
      try {
        return await edrGet<EnrollmentDetail>(`${BASE}/${encodeURIComponent(id!)}`);
      } catch (err) {
        if (err instanceof EdrApiError && err.status === 404) return null;
        throw err;
      }
    },
  });
}

export function useMintEnrollment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: MintEnrollmentInput): Promise<MintEnrollmentResult> => {
      return await edrPost<MintEnrollmentResult>(BASE, {
        label: input.label?.trim() || undefined,
        platform: input.platform,
        expiresInHours: input.expiresInHours,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
    },
    onError: (err) => {
      if (err instanceof EdrApiError && err.status === 404) {
        toast.message("Enrollment API not yet available", {
          description: "The agent-enrollment endpoint hasn't shipped yet.",
        });
        return;
      }
      const message = err instanceof Error ? err.message : "Failed to mint enrollment code";
      toast.error(message);
    },
  });
}

export function useRevokeEnrollment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await edrPatch(`${BASE}/${encodeURIComponent(id)}`, { status: "revoked" });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      toast.success("Enrollment revoked");
    },
    onError: (err) => {
      if (err instanceof EdrApiError && err.status === 404) {
        toast.message("Revoke not yet available", {
          description: "The agent-enrollment endpoint hasn't shipped yet.",
        });
        return;
      }
      const message = err instanceof Error ? err.message : "Failed to revoke enrollment";
      toast.error(message);
    },
  });
}

/** Manually refresh a single enrollment's status and patch the list cache. */
export function useRefreshEnrollment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<EnrollmentDetail> => {
      const detail = await edrGet<EnrollmentDetail>(
        `${BASE}/${encodeURIComponent(id)}`,
      );
      qc.setQueryData<EnrollmentListResult>(LIST_KEY, (old) => {
        if (old?.kind !== "ok") return old;
        return {
          kind: "ok",
          rows: old.rows.map((r) =>
            r.id === id ? ({ ...r, ...detail } as EnrollmentSummary) : r,
          ),
        };
      });
      qc.setQueryData(["edr", "enrollments", "detail", id], detail);
      return detail;
    },
    onError: (err) => {
      if (err instanceof EdrApiError && err.status === 404) {
        toast.message("Refresh not yet available", {
          description: "The agent-enrollment endpoint hasn't shipped yet.",
        });
        return;
      }
      const message = err instanceof Error ? err.message : "Failed to refresh status";
      toast.error(message);
    },
  });
}
